import { InvoiceRollup, RollupEntity } from '@marmon/shared/types/analysis';
import { QueryConstraintMap } from '@marmon/shared/types/common';
import {
	QuadEightyTwenty,
	QuadEightyTwentyRange,
} from '@marmon/shared/types/quad';
import { BreakdownSectionGroup, BreakdownSectionOption } from '../components';

const LabelPrefix = (entity: RollupEntity) =>
	`customer-product.${entity}.breakdown`;

const BreakdownOption = (
	entity: RollupEntity,
	et: QuadEightyTwenty,
	queryConstraints: QueryConstraintMap<InvoiceRollup>[],
	prefix: string,
): BreakdownSectionOption => ({
	label: `${LabelPrefix(entity)}.${et}-${entity}`,
	appearance: et === '20' ? 'red' : 'green',
	key: `${prefix}.${et}`,
	queryConstraints: [
		et === '80'
			? {
					property: 'quad',
					op: '<=',
					value: QuadEightyTwentyRange.Eighty,
				}
			: {
					property: 'quad',
					op: '>',
					value: QuadEightyTwentyRange.Eighty,
				},
		...queryConstraints,
	],
});

type CreateBreakdownSectionOptions = {
	label?: boolean;
	sublabel?: string;
	key: string;
	queryConstraints: {
		[key in QuadEightyTwenty]: QueryConstraintMap<InvoiceRollup>[];
	};
	value?: number;
	valueType?: 'percent' | 'dollars';
};

export const CreateBreakdownSection = (options: {
	entity: RollupEntity;
	key: string;
	sections: CreateBreakdownSectionOptions[];
}): BreakdownSectionGroup => ({
	header: `${LabelPrefix(options.entity)}.${options.key}.header`,
	key: options.key,
	sections: options.sections.map((section) => ({
		key: section.key,
		value: section.value,
		valueType: section.valueType,
		label: section.label
			? `${LabelPrefix(options.entity)}.${options.key}.${section.key}`
			: undefined,
		sublabel: section.sublabel,
		options: Object.entries(section.queryConstraints).map(
			([key, queryConstraints]) =>
				BreakdownOption(
					options.entity,
					key as QuadEightyTwenty,
					queryConstraints,
					`${options.key}.${section.key}`,
				),
		),
	})),
});

export const CreatePercentileSection = (options: {
	key: string;
	property: keyof InvoiceRollup;
	value: number;
	valueType: 'percent' | 'dollars';
	sublabel?: string;
}): CreateBreakdownSectionOptions => ({
	label: true,
	sublabel: options.sublabel,
	value: options.value,
	valueType: options.valueType,
	key: options.key,
	queryConstraints: {
		80: [{ property: options.property, op: '<', value: options.value }],
		20: [{ property: options.property, op: '<', value: options.value }],
	},
});

export const CreateOverviewSection = (entity: RollupEntity) =>
	CreateBreakdownSection({
		entity,
		key: 'overview',
		sections: [
			{
				key: 'all',
				label: true,
				// left empty to return all
				queryConstraints: { 80: [], 20: [] },
			},
			{
				key: 'bought-20',
				label: true,
				queryConstraints: {
					80: [
						{ property: 'count80', op: '==', value: 0 },
						{ property: 'count20', op: '!=', value: 0 },
					],
					20: [
						{ property: 'count80', op: '==', value: 0 },
						{ property: 'count20', op: '!=', value: 0 },
					],
				},
			},
		],
	});
